/**
 * Global keyboard shortcuts, Tally-style.
 *
 * Two problems this solves that scattered onKeyDown handlers cannot:
 *
 * 1. BROWSERS RESERVE THE FUNCTION KEYS. Tally uses F4-F9 for voucher
 *    types, but F5 reloads, F6 jumps to the address bar, F11 goes
 *    fullscreen and F1 opens help. We remap to Alt+number, which is free
 *    in every major browser and keeps the digit users already know:
 *    Tally F5 (Payment) -> Alt+5.
 *
 * 2. SCOPING. When a dialog is open, the screen behind it must stop
 *    responding to shortcuts. Handlers are registered on a stack and only
 *    the topmost scope receives keys, so opening a modal automatically
 *    suspends everything beneath it.
 *
 * Shortcuts are also suppressed while the user is typing in a field,
 * unless the binding explicitly opts in (Escape, Ctrl+Enter, Alt+letter),
 * so typing "5" into an amount box never navigates away.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  type ReactNode,
} from 'react'

export interface Hotkey {
  /** e.g. 'alt+5', 'ctrl+enter', 'escape', 'alt+c' */
  combo: string
  handler: (e: KeyboardEvent) => void
  /** Human-readable, shown in the on-screen legend. */
  label?: string
  /** Allow firing while an input is focused. Default false. */
  allowInInput?: boolean
  /** Prevent the browser default. Default true. */
  preventDefault?: boolean
}

interface HotkeyValue {
  /** Register hotkeys for a scope. Returns an unregister function. */
  register: (scopeId: string, keys: Hotkey[]) => () => void
  /** Bindings of the active scope, for rendering a key legend. */
  active: () => Hotkey[]
}

const Ctx = createContext<HotkeyValue | null>(null)

function normalise(e: KeyboardEvent): string {
  const parts: string[] = []
  if (e.ctrlKey || e.metaKey) parts.push('ctrl')
  if (e.altKey) parts.push('alt')
  if (e.shiftKey) parts.push('shift')
  let key = e.key.toLowerCase()
  if (key === ' ') key = 'space'
  // Alt+digit reports the digit on most layouts; Alt+letter may report a
  // composed character, so fall back to the physical code.
  if (e.altKey && e.code.startsWith('Key')) key = e.code.slice(3).toLowerCase()
  if (e.altKey && e.code.startsWith('Digit')) key = e.code.slice(5)
  parts.push(key)
  return parts.join('+')
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
    el.isContentEditable
}

export function HotkeyProvider({ children }: { children: ReactNode }) {
  // Stack of scopes; the last entry wins.
  const stack = useRef<{ id: string; keys: Hotkey[] }[]>([])

  const register = useCallback((scopeId: string, keys: Hotkey[]) => {
    const entry = { id: scopeId, keys }
    stack.current.push(entry)
    return () => {
      stack.current = stack.current.filter((s) => s !== entry)
    }
  }, [])

  const active = useCallback(
    () => stack.current[stack.current.length - 1]?.keys ?? [], [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const top = stack.current[stack.current.length - 1]
      if (!top) return
      const combo = normalise(e)
      const hit = top.keys.find((k) => k.combo.toLowerCase() === combo)
      if (!hit) return
      if (isTyping(e.target) && !hit.allowInInput) {
        // Modified combos are safe to fire even inside a field.
        const modified = e.altKey || e.ctrlKey || e.metaKey
        if (!modified) return
      }
      if (hit.preventDefault !== false) e.preventDefault()
      hit.handler(e)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const value = useMemo<HotkeyValue>(() => ({ register, active }), [register, active])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useHotkeyContext(): HotkeyValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useHotkeys must be used inside <HotkeyProvider>')
  return v
}

/**
 * Register hotkeys for the lifetime of a component.
 *
 *   useHotkeys('home', [
 *     { combo: 'alt+5', label: 'Payment', handler: () => nav('/vouchers/payment') },
 *   ])
 */
export function useHotkeys(scopeId: string, keys: Hotkey[], enabled = true) {
  const { register } = useHotkeyContext()
  // Keep the latest handlers without re-registering on every render.
  const ref = useRef(keys)
  ref.current = keys

  useEffect(() => {
    if (!enabled) return
    const proxied: Hotkey[] = keys.map((k, i) => ({
      ...k,
      handler: (e) => ref.current[i]?.handler(e),
    }))
    return register(scopeId, proxied)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, enabled, register, keys.map((k) => k.combo).join('|')])
}

/** Display form for a combo: 'alt+5' -> 'Alt+5'. */
export function prettyCombo(combo: string): string {
  return combo
    .split('+')
    .map((p) => (p.length === 1 ? p.toUpperCase() : p[0].toUpperCase() + p.slice(1)))
    .join('+')
}
